import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { DollarSign, ShoppingCart, Users, TrendingUp, Loader2, Eye, UserCheck, BarChart3, Download, FileSpreadsheet, FileText, File, HelpCircle } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { DateRangeSelector } from '../components/DateRangeSelector';
import { toast } from 'sonner';

const TYPE_COLORS = {
  Ebook: '#195259', Course: '#1D6B75', Template: '#FF9A00', Preset: '#6D6E71', Poster: '#D4883A', Bundle: '#0F3338'
};

export default function SalesReportPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const loadReport = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const res = await reportsAPI.summary(params);
      setSummary(res.data);
    } catch (err) {
      // Report loading failed - UI handles empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(dateFrom, dateTo); }, [loadReport, dateFrom, dateTo]);

  const handleRangeChange = useCallback((from, to) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const getRangeLabel = () => {
    if (!dateFrom && !dateTo) return 'All Time';
    if (dateFrom && dateTo) {
      return `${dateFrom} to ${dateTo}`;
    }
    return 'Custom range';
  };

  const handleExport = async (format) => {
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await reportsAPI.exportReport(format, params);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'xlsx' ? 'xlsx' : format;
      a.download = `funnely-sales-report.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} report downloaded!`);
    } catch (err) {
      toast.error('Export failed. Please try again.');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!summary) return <div className="text-center py-20 text-muted-foreground">Failed to load report</div>;

  const kpis = [
    { label: 'Total Revenue', desc: 'Total sales amount collected from all paid orders within the selected date range.', value: `RM ${(summary.total_revenue || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`, icon: DollarSign, bg: '#195259' },
    { label: 'Total Orders', desc: 'Number of completed orders placed by customers. Includes all payment methods.', value: summary.total_orders || 0, icon: ShoppingCart, bg: '#FF9A00' },
    { label: 'Total Customers', desc: 'Number of unique customers who have purchased from your store.', value: summary.total_customers || 0, icon: Users, bg: '#195259' },
    { label: 'Avg. Order Value', desc: 'Average amount spent per order. Calculated as Total Revenue divided by Total Orders.', value: summary.total_orders > 0 ? `RM ${(summary.total_revenue / summary.total_orders).toFixed(2)}` : 'RM 0.00', icon: TrendingUp, bg: '#FF9A00' },
  ];

  const visitorKpis = [
    { label: 'Total Page Views', desc: 'Total number of times your landing pages were viewed, including repeat visits.', value: (summary.total_visitors || 0).toLocaleString(), icon: Eye, bg: '#195259' },
    { label: 'Unique Visitors', desc: 'Number of distinct visitors to your landing pages. Each visitor is counted once.', value: (summary.total_unique_visitors || 0).toLocaleString(), icon: UserCheck, bg: '#FF9A00' },
    { label: 'Overall Conversion Rate', desc: 'Percentage of unique visitors who completed a purchase. Higher is better — above 3% is good!', value: `${summary.overall_conversion_rate || 0}%`, icon: BarChart3, bg: '#195259' },
  ];

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales Report</h1>
          <p className="text-sm text-muted-foreground mt-1">Overview of your store performance</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2" data-testid="export-report-button">
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">
                <FileText className="h-4 w-4 mr-2" /> Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Export as Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                <File className="h-4 w-4 mr-2" /> Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DateRangeSelector onRangeChange={handleRangeChange} defaultPreset="last30" />
        </div>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="card-hover" data-testid={kpi.label === 'Total Revenue' ? 'sales-report-kpi-mrr' : kpi.label === 'Total Orders' ? 'sales-report-kpi-orders' : undefined}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm text-muted-foreground">{kpi.label}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help hover:text-muted-foreground transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs leading-relaxed">{kpi.desc}</TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-2xl font-semibold mt-1" style={{ color: '#195259' }}>{kpi.value}</p>
                  </div>
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: kpi.bg }}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Visitor KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {visitorKpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm text-muted-foreground">{kpi.label}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help hover:text-muted-foreground transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs leading-relaxed">{kpi.desc}</TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-2xl font-semibold mt-1" style={{ color: '#195259' }}>{kpi.value}</p>
                  </div>
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: kpi.bg }}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Revenue Trend */}
        <Card className="lg:col-span-8" data-testid="sales-report-revenue-chart">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Revenue Trend</CardTitle>
            <Badge variant="secondary">{getRangeLabel()}</Badge>
          </CardHeader>
          <CardContent>
            {summary.revenue_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={summary.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#D4C9B0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip formatter={(value) => [`RM ${value.toFixed(2)}`, 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(187, 56%, 22%)" fill="rgba(25,82,89,0.12)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>No sales data yet</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Orders by Type */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Sales by Product Type</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.orders_by_type?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={summary.orders_by_type} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#D4C9B0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="type" tick={{ fontSize: 12 }} width={80} />
                    <RechartsTooltip formatter={(value) => [value, 'Orders']} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {summary.orders_by_type.map((entry, i) => (
                        <Cell key={`type-${entry.type}-${i}`} fill={TYPE_COLORS[entry.type] || '#6B7280'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-4">
                  {summary.orders_by_type.map(t => (
                    <div key={t.type} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: TYPE_COLORS[t.type] || '#6B7280' }} />
                        <span>{t.type}</span>
                      </div>
                      <span className="font-mono text-muted-foreground">RM {(t.revenue || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conversion Rate by Landing Page */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Conversion Rate by Landing Page</CardTitle>
          <p className="text-xs text-muted-foreground">How each landing page converts visitors to customers</p>
        </CardHeader>
        <CardContent>
          {(summary.conversion_by_page || []).length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Landing Page</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Unique Visitors</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Conversion Rate</TableHead>
                  <TableHead className="w-[140px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.conversion_by_page.map((page) => (
                  <TableRow key={page.page_id}>
                    <TableCell className="font-medium">{page.page_title}</TableCell>
                    <TableCell className="text-right">{page.views.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{page.unique_visitors.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{page.orders}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={page.conversion_rate > 5 ? 'default' : page.conversion_rate > 0 ? 'secondary' : 'outline'}>
                        {page.conversion_rate}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Progress value={Math.min(page.conversion_rate, 100)} className="h-2" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <Eye className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>No page view data yet</p>
              <p className="text-xs mt-1">Visitor analytics will appear once your landing pages get traffic</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Products */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Top Selling Products</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.top_products?.length > 0 ? (
            <div className="space-y-4">
              {summary.top_products.map((product, i) => {
                const maxRev = summary.top_products[0]?.total_revenue || 1;
                return (
                  <div key={product.id} className="flex items-center gap-4">
                    <span className="text-lg font-semibold text-muted-foreground w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <span className="text-sm font-semibold">RM {(product.total_revenue || 0).toFixed(2)}</span>
                      </div>
                      <Progress value={((product.total_revenue || 0) / maxRev) * 100} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">{product.total_sales || 0} sales &middot; {product.type}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No products sold yet</p>
          )}
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
}
